import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { Alert, AlertTitle, Button, Stack, Typography } from '@mui/material';
import Iconify from 'src/components/Iconify';
import PinLeadDialog from 'src/components/dialogPopUp/PinLeadDialog';
import { piiProps } from 'src/components/DemoBlur';
import { usePopupFailure } from 'src/contexts/PopupFailureContext';
import useLeadPins from 'src/hooks/useLeadPins';
import { dispatch } from 'src/redux/store';
import { attachPinError, findPinForLead, pinLead } from 'src/redux/slices/leadPins';

// -----------------------------------------------
// Shown inside an action popup after something fails (see PopupFailureProvider).
// Offers to pin the lead with the error attached, or — when the lead is already
// pinned — to add the error to the existing pin.

export default function PopupFailureBanner({ leadId, leadPracticeId }) {
    const { latestFailure, dismiss } = usePopupFailure();
    const { practiceId, pins, loaded } = useLeadPins();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    // The failure the dialog was opened for: frozen so a newer failure arriving
    // while the dialog is open can't change what is shown or submitted.
    const [dialogFailure, setDialogFailure] = useState(null);
    // Synchronous in-flight guard — `saving` only updates after a re-render, so
    // a fast double-click could otherwise fire twice.
    const inFlight = useRef(false);

    if (!latestFailure) return null;

    const otherPractice = leadPracticeId != null && practiceId != null && String(leadPracticeId) !== String(practiceId);
    const canAct = Boolean(leadId && practiceId && loaded && !otherPractice);
    const pin = canAct ? findPinForLead(pins, leadId) : null;

    const run = async (action) => {
        if (inFlight.current) return false;
        inFlight.current = true;
        setSaving(true);
        try {
            return await action();
        } catch (err) {
            // The pin thunks report their own failures; this only guards an
            // unexpected rejection from leaving the buttons stuck.
            return false;
        } finally {
            inFlight.current = false;
            setSaving(false);
        }
    };

    const openDialog = () => {
        setDialogFailure(latestFailure);
        setDialogOpen(true);
    };

    const handlePin = async (reason) => {
        const failureToPin = dialogFailure || latestFailure;
        const ok = await run(() => dispatch(pinLead(practiceId, Number(leadId) || leadId, reason, undefined, failureToPin)));
        if (ok) {
            setDialogOpen(false);
            setDialogFailure(null);
            dismiss();
        }
        // On failure keep the banner (and its failure): a 400 "already pinned"
        // resyncs the list, after which the pin exists and the action below
        // switches to "Add this error to the pin".
    };

    const handleAttach = async () => {
        if (!pin) return;
        const ok = await run(() => dispatch(attachPinError(practiceId, pin.id, latestFailure)));
        if (ok) dismiss();
    };

    return (
        <>
            <Alert
                severity="error"
                data-testid="popup-failure-banner"
                sx={{
                    mb: 2,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    '& .MuiAlert-message': { flex: '1 1 320px', minWidth: 0 },
                    '& .MuiAlert-action': { flexShrink: 0, ml: 'auto', pl: 0, flexWrap: 'wrap' },
                }}
                action={(
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 0.25, flexWrap: 'wrap', rowGap: 1 }}>
                        {canAct && !pin && (
                            <Button
                                size="small"
                                variant="contained"
                                disabled={saving}
                                startIcon={<Iconify icon="mdi:pin" width={16} />}
                                onClick={openDialog}
                                sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderRadius: '8px' }}
                            >
                                Pin lead &amp; resolve later
                            </Button>
                        )}
                        {canAct && pin && (
                            <Button
                                size="small"
                                variant="contained"
                                disabled={saving}
                                startIcon={<Iconify icon="mdi:pin" width={16} />}
                                onClick={handleAttach}
                                sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderRadius: '8px' }}
                            >
                                Add this error to the pin
                            </Button>
                        )}
                        <Button size="small" color="inherit" onClick={dismiss} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                            Dismiss
                        </Button>
                    </Stack>
                )}
            >
                <AlertTitle>Something went wrong</AlertTitle>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }} {...piiProps}>{latestFailure.message}</Typography>
                {canAct && !pin && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Pin this lead so you can come back and carry on once it&apos;s fixed. We&apos;ll attach this error to the pin.
                    </Typography>
                )}
            </Alert>
            {canAct && !pin && (
                <PinLeadDialog
                    open={dialogOpen}
                    mode="create"
                    loading={saving}
                    attachedError={dialogFailure || latestFailure}
                    onClose={() => { setDialogOpen(false); setDialogFailure(null); }}
                    onSubmit={handlePin}
                />
            )}
        </>
    );
}

PopupFailureBanner.propTypes = {
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadPracticeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

PopupFailureBanner.defaultProps = {
    leadId: null,
    leadPracticeId: null,
};
